import React, { createContext, useContext, useState, useCallback } from 'react';
import { colors, spacing, radius, fontSize } from './theme';
import { CheckCircle, AlertTriangle, X } from 'lucide-react';

const ToastContext = createContext(null);

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const colorMap = {
    success: { bg: colors.successBg, text: colors.success, icon: CheckCircle },
    error: { bg: colors.errorBg, text: colors.error, icon: AlertTriangle },
    warning: { bg: colors.warningBg, text: colors.warning, icon: AlertTriangle },
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div style={{
        position: 'fixed', top: 80, right: 16, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {toasts.map(t => {
          const c = colorMap[t.type] || colorMap.success;
          const Icon = c.icon;
          return (
            <div key={t.id} style={{
              background: c.bg, color: c.text, borderRadius: radius.md,
              padding: '10px 14px', fontSize: fontSize.sm, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxWidth: 360, animation: 'slideIn 0.2s ease-out',
            }}>
              <Icon size={16} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{t.message}</span>
              <button onClick={() => removeToast(t.id)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: c.text, opacity: 0.6,
              }}><X size={14} /></button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
